import { type PhoneProviderProps } from '@hit/react-jssip';
import { useAtomValue, useSetAtom } from 'jotai';

import { usePause } from '@/api/pauses';
import { useHasQueueMetrics } from '@/api/use-branch-features';
import { defaultPauseReasons } from '@/config/default-pause-reasons';
import { useExtension } from '@/hooks/use-extension';
import {
  notifyDialogAtom,
  numberOfMissedCallsToPauseAtom,
} from '@/store/atoms';

let prevOrigin = '';
let prevTimestamp = 0;

export const useOnMissedCall = () => {
  const extension = useExtension();
  const hasQueueMetrics = useHasQueueMetrics();
  const { mutate: pause } = usePause();
  const setNotifyDialog = useSetAtom(notifyDialogAtom);
  const numberOfMissedCallsToPause = useAtomValue(
    numberOfMissedCallsToPauseAtom
  );
  const pauseOnMissedCallEnabled = numberOfMissedCallsToPause >= 1;

  const onMissedCall: PhoneProviderProps['onMissedCall'] = (missedCall) => {
    if (
      hasQueueMetrics !== true ||
      missedCall.cause !== 'timeout' ||
      !extension?.number ||
      !pauseOnMissedCallEnabled
    ) {
      return;
    }

    const origin = missedCall.from.number;
    const timestamp = Date.now();

    if (!prevOrigin) {
      prevOrigin = origin;
    }
    if (!prevTimestamp) {
      prevTimestamp = timestamp;
    }

    const maxTimeBetweenCalls = 1000 * 60 * 60; // 1 hour

    const originsAreDifferent = origin !== prevOrigin;
    const timeBetweenCalls = timestamp - prevTimestamp;

    if (originsAreDifferent || timeBetweenCalls >= maxTimeBetweenCalls) {
      localStorage.setItem('missedCalls', '0');
    }

    const initialMissedCalls = Number(localStorage.getItem('missedCalls')) || 0;
    const missedCalls = initialMissedCalls + 1;

    if (missedCalls >= numberOfMissedCallsToPause) {
      pause(
        {
          extension: extension.number,
          reasonCode: defaultPauseReasons.NOT_ANSWERED_PAUSE,
        },
        {
          onSuccess: () =>
            setNotifyDialog({ open: true, message: 'missedCallPauseMessage' }),
        }
      );

      localStorage.setItem('missedCalls', '0');
    } else {
      localStorage.setItem('missedCalls', missedCalls.toString());
    }

    prevOrigin = origin;
    prevTimestamp = Date.now();
  };

  return onMissedCall;
};
